import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { FormProvider } from "react-hook-form";

import Errors from "@/components/Errors";
import AddressFinderSearch from "@/components/shared/AddressFinderSearch";
import { FadeTransition } from "@/components/animations/FadeTransition";
import ConnectedBadge from "@/components/organisations/shared/ui/ConnectedBadge";
import DisconnectedBadge from "@/components/organisations/shared/ui/DisconnectedBadge";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { RESEND_VERIFICATION_EMAIL } from "@/graphql/accounts";
import {
  REMOVE_PATIENT_FROM_ORGANISATION_MUTATION,
  REQUEST_ACCESS_TO_RESULTS_MUTATION,
  RESEND_REQUEST_TO_ACCESS_RESULTS_MUTATION,
  SAVE_PATIENT_SHIPPING_ADDRESS_MUTATION
} from "@/graphql/organisations/mutations";
import {
  CONNECTED_USERS_QUERY,
  ORGANISATION_PATIENT_SHIPPING_ADDRESS_QUERY
} from "@/graphql/organisations/queries/patients";
import useDjangoGraphqlForm from "@/hooks/form/useDjangoGraphqlForm";
import useShippingCountries from "@/hooks/useShippingCountries";
import Center from "@/tpo/Center";
import ControlledFormField from "@/tpo/ControlledFormField";
import EditFormButtons from "@/tpo/EditFormButtons";
import FloatingLabelInput from "@/tpo/FloatingLabelInput";
import Menu from "@/tpo/Menu";
import Modal from "@/tpo/Modal";
import ShippingCountrySelect from "@/tpo/ShippingCountrySelect";
import Stack from "@/tpo/Stack";
import isProxyEmail from "@/utils/isProxyEmail";
import { getShippingAddressFieldNames } from "@/utils/idealPostcodesAddress";
import ButtonV2, { ActionIcon } from "@/v2/Buttons";
import { useLazyQuery, useMutation } from "@apollo/client";

import {
  CMP_BUSINESS_TAB_URL,
  CMP_PATIENTS_TEST_RESULTS_URL,
  getCmpUrl
} from "../../../../core/urls";
import { useDataLoader } from "../../../../hooks/useDataLoader";
import { PanelBoxV2 } from "../../../../tpo/Boxes";
import ChevronComponent from "../../../../tpo/Chevron";
import Group from "../../../../tpo/Group";
import { List, ListContext } from "../../../../tpo/List";
import SimpleTable from "../../../../tpo/SimpleTable";
import Table, { useTableControls } from "../../../../tpo/Table";
import Box from "../../../Box";
import { ExternalTextLink, InternalTextLink } from "../../../Links";
import Text, { SpanText } from "../../../Text";
import InactiveBadge from "../../shared/ui/InactiveBadge";

function getShippingAddressDefaultValues(data) {
  return {
    name: data?.name || "",
    line1: data?.line1 || "",
    line2: data?.line2 || "",
    city: data?.city || "",
    postcode: data?.postcode || "",
    country: data?.country?.id || "",
    phoneNumber: data?.phoneNumber || ""
  };
}

function PatientShippingAddressEditForm({ patientId, hasShippingAddress }) {
  const { organisation } = useOrganisationContext();
  const shippingCountries = useShippingCountries();

  const submitButtonRef = useRef();

  const [showModal, setShowModal] = useState(false);
  const [loadedShippingAddress, setLoadedShippingAddress] = useState(null);

  const shouldLoadExistingAddress = hasShippingAddress || !!loadedShippingAddress?.id;

  const [
    fetchShippingAddress,
    { loading: shippingAddressLoading, error: shippingAddressError }
  ] = useLazyQuery(ORGANISATION_PATIENT_SHIPPING_ADDRESS_QUERY, {
    fetchPolicy: "network-only",
    onCompleted: data => {
      setLoadedShippingAddress(data.organisationPatientShippingAddress);
    }
  });

  const loadShippingAddress = useCallback(() => {
    fetchShippingAddress({
      variables: {
        patientId,
        organisationId: organisation.id
      }
    });
  }, [fetchShippingAddress, organisation.id, patientId]);

  const { methods, onSubmit, nonFieldError } = useDjangoGraphqlForm({
    mutation: SAVE_PATIENT_SHIPPING_ADDRESS_MUTATION,
    mutationName: "savePatientShippingAddressMutation",
    defaultValues: getShippingAddressDefaultValues(null),
    transformer: data => ({
      ...data,
      id: loadedShippingAddress?.id,
      patient: patientId,
      organisation: organisation.id
    }),
    handleSuccess: ({ data, api: { reset } }) => {
      const address = data.savePatientShippingAddressMutation.address;
      setLoadedShippingAddress(address);
      reset(getShippingAddressDefaultValues(address));
      setShowModal(false);
      if (submitButtonRef.current) {
        submitButtonRef.current.setSuccessful(true);
        submitButtonRef.current.setPending(false);
      }
    },
    handleFailure: () => {
      if (submitButtonRef.current) {
        submitButtonRef.current.setSuccessful(false);
        submitButtonRef.current.setPending(false);
      }
    }
  });

  useEffect(() => {
    if (!showModal) {
      methods.reset(getShippingAddressDefaultValues(null));
      return;
    }

    if (shouldLoadExistingAddress) {
      loadShippingAddress();
    }
  }, [showModal, shouldLoadExistingAddress, loadShippingAddress, methods]);

  useEffect(() => {
    if (
      showModal &&
      !shippingAddressLoading &&
      !(shippingAddressError && shouldLoadExistingAddress)
    ) {
      methods.reset(getShippingAddressDefaultValues(loadedShippingAddress));
    }
  }, [
    showModal,
    shippingAddressLoading,
    shippingAddressError,
    shouldLoadExistingAddress,
    loadedShippingAddress,
    methods
  ]);

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <>
      <ButtonV2
        color="dark"
        size={["xs"]}
        mr="auto"
        rightIcon={<ChevronComponent />}
        onClick={handleOpenModal}
      >
        shipping address
      </ButtonV2>
      <Modal
        mode={["fullScreen", "fullScreen", "centered"]}
        maxWidth={1020}
        bg="white"
        closeButton
        headerProps={{
          p: [20, 20, 40]
        }}
        show={showModal}
        close={handleCloseModal}
        data-component-name="PatientShippingAddressModal"
      >
        <PanelBoxV2
          maxWidth={760}
          outer={{
            bg: "white",
            px: [20, 20, 40],
            pt: [20, 20, 40],
            pb: [50, 50, 80],
            borderRadius: 5
          }}
          stacked
          gap={40}
        >
          {shippingAddressLoading ? (
            <Center py={40}>
              <Text>Loading shipping address...</Text>
            </Center>
          ) : shippingAddressError && shouldLoadExistingAddress ? (
            <Stack gap={20} alignItems="center" py={40}>
              <Errors>Unable to load shipping address. Please try again.</Errors>
              <ButtonV2 color="dark" onClick={loadShippingAddress}>
                Try again
              </ButtonV2>
            </Stack>
          ) : (
          <FormProvider {...methods} submitButtonRef={submitButtonRef}>
            <Box fontFamily="gilroyBold" fontSize={28}>
              Patient shipping address
            </Box>
            <Stack as="form" gap={[20, 20, 40]} onSubmit={methods.handleSubmit(onSubmit)}>
              <AddressFinderSearch
                shippingCountries={shippingCountries}
                fieldNames={getShippingAddressFieldNames()}
                countryValueType="id"
              />
              <ControlledFormField name="name" Component={FloatingLabelInput} label="Name" />
              <ControlledFormField
                name="line1"
                Component={FloatingLabelInput}
                label="Address line 1"
              />
              <ControlledFormField
                name="line2"
                Component={FloatingLabelInput}
                label="Address line 2"
              />
              <ControlledFormField name="city" Component={FloatingLabelInput} label="City" />
              <ControlledFormField
                name="postcode"
                Component={FloatingLabelInput}
                label="Postcode"
              />
              <ControlledFormField
                name="phoneNumber"
                Component={FloatingLabelInput}
                label="Phone number"
              />
              <ControlledFormField
                name="country"
                valueField="id"
                placeholder="Country"
                fullWidth
                Component={ShippingCountrySelect}
              />
              <Center gap={20} mt={20}>
                <EditFormButtons />
              </Center>
              <FadeTransition in={!!nonFieldError}>
                <Errors>{nonFieldError}</Errors>
              </FadeTransition>
            </Stack>
          </FormProvider>
          )}
        </PanelBoxV2>
      </Modal>
    </>
  );
}

const renderers = {
  personalDetails: ({ id, email, patientProfile, granted, hasShippingAddress, isAdmin }) => {
    return (
      <Box display="flex" flexDirection="column" gap={10}>
        {!!(patientProfile?.user?.firstName && patientProfile?.user?.lastName) && (
          <Group>
            <Text fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
              {`${patientProfile?.user?.firstName} ${patientProfile?.user?.lastName}`}
            </Text>
            {!!patientProfile?.guardian && (
              <Text fontSize={[14, 14, 16]} color="grey" fontStyle="italic">
                &nbsp;(c/o {patientProfile?.guardian?.user?.firstName}&nbsp;
                {patientProfile?.guardian?.user?.lastName})
              </Text>
            )}
          </Group>
        )}
        {!!patientProfile?.guardian ? (
          <Text fontSize={[14, 14, 16]}>{patientProfile.guardian.user.email}</Text>
        ) : (
          !!email && !isProxyEmail(email) && <Text fontSize={[14, 14, 16]}>{email}</Text>
        )}

        {granted && isAdmin && (
          <PatientShippingAddressEditForm patientId={id} hasShippingAddress={hasShippingAddress} />
        )}
      </Box>
    );
  },
  patientPortal: ({ isActive, guardianIsActive }) => {
    return isActive || guardianIsActive ? (
      <ConnectedBadge key="connected" text="active" mr="auto" />
    ) : (
      <InactiveBadge key="inactive" mr="auto" />
    );
  },
  resultAccess: ({ granted, relatedResultsAccessLinkId }) => {
    const badges = [];

    if (relatedResultsAccessLinkId) {
      if (granted) {
        badges.push(<ConnectedBadge key="connected" text="granted" />);
      } else if (granted == false) {
        badges.push(<DisconnectedBadge key="disconnected" text="revoked" />);
      } else if (granted == null) {
        badges.push(<InactiveBadge key="inactive" text="requested" />);
      }
    }

    if (badges.length === 0) {
      badges.push(<InactiveBadge key="inactive" text="requested" />);
    }

    return (
      badges.length > 0 && (
        <Group gap={10} alignItems="center" mx={[0, 0, 2]}>
          {badges.map(b => b)}
        </Group>
      )
    );
  },
  results: ({
    id,
    patientProfile,
    relatedResultsAccessLinkId,
    granted,
    isAdmin,
    patientProfile: {
      user: { isActive },
      guardian
    },
  }) => {
    return (
      <StatusBasedButton
        id={id}
        patientProfile={patientProfile}
        relatedResultsAccessLinkId={relatedResultsAccessLinkId}
        granted={granted}
        isActive={isActive}
        hasGuardian={!!guardian}
        isAdmin={isAdmin}
        guardianIsActive={guardian?.user?.isActive}
      />
    );
  }
};

function RequestAccessToResults({ id, patientProfile, show, onClose }) {
  const { organisation } = useOrganisationContext();

  const [requestAccessMutation] = useMutation(REQUEST_ACCESS_TO_RESULTS_MUTATION, {
    update: (cache, { data }) => {
      if (data?.requestAccessToResultsMutation?.errors.length === 0) {
        cache.modify({
          id: `OrganisationPatientType:${id}`,
          fields: {
            relatedResultsAccessLinkId: () => {
              return data?.requestAccessToResultsMutation?.resultsAccessLink?.id;
            },
            granted: () => {
              return null;
            }
          }
        });
      }
    }
  });

  const requestAccess = useCallback(() => {
    requestAccessMutation({
      variables: {
        input: {
          // NB - organisation field is only for perm checking on server side
          organisation: organisation?.id,
          patientProfile: patientProfile?.pk
        }
      }
    }).catch(e => console.log("Error encountered requesting access", e));
  }, [requestAccessMutation, patientProfile?.pk, organisation]);

  return (
    <>
      <Modal
        mode={["fullScreen", "fullScreen", "centered"]}
        maxWidth={640}
        bg="white"
        closeButton={null}
        show={show}
        close={onClose}
        data-component-name="RequestAccessToResultsModal"
        borderRadius={[0, 0, 20]}
      >
        <Stack gap={40} mx="auto" maxWidth={720} width="100%" px={[40]} pt={[60]} pb={[80]}>
          <Stack gap={20}>
            <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]} textAlign="center">
              Results access
            </Box>
            <Box
              fontFamily="gilroyMedium"
              fontSize={[14, 14, 16]}
              lineHeight={["28px"]}
              textAlign="center"
            >
              You are about to request access to a patient&apos;s records. They will be sent an
              invitation to connect and will need to grant you permission before you can see their
              profile and results.
            </Box>
          </Stack>
          <Center gap={20}>
            <ButtonV2 color="red" size={["sm", "sm", "md"]} onClick={onClose} withChevron>
              cancel
            </ButtonV2>
            <ButtonV2
              color="green"
              size={["sm", "sm", "md"]}
              onClick={() => {
                requestAccess();
                onClose();
              }}
              withChevron
            >
              send request email
            </ButtonV2>
          </Center>
        </Stack>
      </Modal>
    </>
  );
}

function MenuActionConfirmModal({
  show,
  onClose,
  onConfirm,
  loading,
  title,
  description,
  confirmLabel,
  dataComponentName
}) {
  return (
    <Modal
      mode={["fullScreen", "fullScreen", "centered"]}
      maxWidth={640}
      bg="white"
      closeButton={null}
      show={show}
      close={onClose}
      data-component-name={dataComponentName}
      borderRadius={[0, 0, 20]}
    >
      <Stack gap={40} mx="auto" maxWidth={720} width="100%" px={[40]} pt={[60]} pb={[80]}>
        <Stack gap={20}>
          <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]} textAlign="center">
            {title}
          </Box>
          <Box
            fontFamily="gilroyMedium"
            fontSize={[14, 14, 16]}
            lineHeight={["28px"]}
            textAlign="center"
          >
            {description}
          </Box>
        </Stack>
        <Center gap={20}>
          <ButtonV2
            color="red"
            size={["sm", "sm", "md"]}
            onClick={onClose}
            disabled={loading}
            withChevron
          >
            cancel
          </ButtonV2>
          <ButtonV2
            color="green"
            size={["sm", "sm", "md"]}
            disabled={loading}
            onClick={() => {
              onConfirm();
              onClose();
            }}
            withChevron
          >
            {confirmLabel}
          </ButtonV2>
        </Center>
      </Stack>
    </Modal>
  );
}

function RemovePatientFromOrganisationModal({ show, onClose, onConfirm, loading }) {
  return (
    <Modal
      mode={["fullScreen", "fullScreen", "centered"]}
      maxWidth={640}
      bg="white"
      closeButton={null}
      show={show}
      close={onClose}
      data-component-name="RemovePatientFromOrganisationModal"
      borderRadius={[0, 0, 20]}
    >
      <Stack gap={40} mx="auto" maxWidth={720} width="100%" px={[40]} pt={[60]} pb={[80]}>
        <Stack gap={20}>
          <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]} textAlign="center">
            Patient removal
          </Box>
          <Box
            fontFamily="gilroyMedium"
            fontSize={[14, 14, 16]}
            lineHeight={["28px"]}
            textAlign="center"
          >
            Once confirmed we will remove this patient from your organisation. They will not be
            deleted from the system and they will still have access to their results if they have an
            active account.
          </Box>
        </Stack>
        <Center gap={20}>
          <ButtonV2
            color="red"
            size={["sm", "sm", "md"]}
            onClick={onClose}
            disabled={loading}
            withChevron
          >
            cancel
          </ButtonV2>
          <ButtonV2
            color="green"
            size={["sm", "sm", "md"]}
            disabled={loading}
            onClick={() => {
              onConfirm();
              onClose();
            }}
            withChevron
          >
            remove
          </ButtonV2>
        </Center>
      </Stack>
    </Modal>
  );
}

function HorizontalDotsIcon(props) {
  return (
    <Box
      as="span"
      display="flex"
      alignItems="center"
      justifyContent="center"
      lineHeight={0}
      {...props}
    >
      <svg
        width={20}
        height={20}
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden
        focusable="false"
      >
        <circle cx="5" cy="12" r="2" />
        <circle cx="12" cy="12" r="2" />
        <circle cx="19" cy="12" r="2" />
      </svg>
    </Box>
  );
}

function StatusBasedButton({
  id,
  patientProfile,
  relatedResultsAccessLinkId,
  granted,
  isActive,
  isAdmin,
  hasGuardian,
  guardianIsActive
}) {
  const { organisation } = useOrganisationContext();
  const organisationId = organisation?.hashId;
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [requestAccessOpen, setRequestAccessOpen] = useState(false);
  const [removePatientConfirmOpen, setRemovePatientConfirmOpen] = useState(false);
  const [resendAccessConfirmOpen, setResendAccessConfirmOpen] = useState(false);
  const [resendVerificationConfirmOpen, setResendVerificationConfirmOpen] = useState(false);

  const [resendRequestToAccessResults, { loading: resendingAccessRequest }] = useMutation(
    RESEND_REQUEST_TO_ACCESS_RESULTS_MUTATION,
    {
      variables: {
        input: {
          organisation: organisation?.id,
          id: relatedResultsAccessLinkId
        }
      }
    }
  );

  const [resendVerificationEmail, { loading: resendingVerification }] = useMutation(
    RESEND_VERIFICATION_EMAIL,
    {
      variables: {
        input: {
          organisationPatient: id
        }
      }
    }
  );

  const [removePatientFromOrganisation, { loading: removingPatient }] = useMutation(
    REMOVE_PATIENT_FROM_ORGANISATION_MUTATION,
    {
      update: (cache, { data }) => {
        if (data?.removePatientFromOrganisationMutation?.errors?.length) {
          return;
        }
        cache.modify({
          id: "ROOT_QUERY",
          fields: {
            connectedUsersPagination(existing, { readField }) {
              if (!existing?.items?.length) {
                return existing;
              }
              const nextItems = existing.items.filter(ref => {
                const itemId = readField("id", ref);
                return itemId == null || String(itemId) !== String(id);
              });
              if (nextItems.length === existing.items.length) {
                return existing;
              }
              return { ...existing, items: nextItems };
            }
          }
        });
        cache.evict({ id: `OrganisationPatientType:${id}` });
        cache.gc();
      },
      onCompleted: data => {
        if (data?.removePatientFromOrganisationMutation?.errors?.length) {
          return;
        }
        setActionsMenuOpen(false);
        setRemovePatientConfirmOpen(false);
      }
    }
  );

  const confirmRemovePatientFromOrganisation = useCallback(() => {
    if (removingPatient || !organisation?.id) {
      return;
    }
    removePatientFromOrganisation({
      variables: {
        input: {
          organisation: organisation.id,
          patient: id
        }
      }
    });
  }, [id, organisation?.id, removePatientFromOrganisation, removingPatient]);

  const handleRemoveFromOrganisation = useCallback(() => {
    setActionsMenuOpen(false);
    setRemovePatientConfirmOpen(true);
  }, []);

  const confirmResendAccessRequest = useCallback(() => {
    resendRequestToAccessResults().catch(e => console.log("Error encountered resending invite", e));
  }, [resendRequestToAccessResults]);

  const confirmResendVerificationEmail = useCallback(() => {
    resendVerificationEmail().catch(e =>
      console.log("Error encountered resending verification email", e)
    );
  }, [resendVerificationEmail]);

  const buttons = [];

  const patientUserId = patientProfile?.user?.id;
  const profileUrl =
    patientUserId &&
    getCmpUrl(CMP_PATIENTS_TEST_RESULTS_URL, {
      organisationId,
      patientId: patientUserId
    });

  const showResendInMenu = !granted && (hasGuardian ? guardianIsActive : isActive) && !!relatedResultsAccessLinkId;
  const showRequestAccessInMenu = !granted && (hasGuardian ? guardianIsActive : isActive) && !relatedResultsAccessLinkId;
  const showResendVerificationInMenu = hasGuardian ? !guardianIsActive : !isActive;
  const showRemoveFromOrgMenu = isAdmin === true;

  const menuButton = (showResendInMenu ||
    showRequestAccessInMenu ||
    showResendVerificationInMenu ||
    showRemoveFromOrgMenu) && (
    <Menu
      key="patient-actions-menu"
      opened={actionsMenuOpen}
      onChange={setActionsMenuOpen}
      trigger="click"
      allowedPlacements={["bottom-end", "bottom-start"]}
      xOffset={0}
      yOffset={4}
    >
      <Menu.Target>
        <ActionIcon size={["sm", "sm", "md"]} bg="haze" variant="circle">
          <HorizontalDotsIcon />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown minWidth={showRequestAccessInMenu ? 260 : 220} py={12}>
        {showResendInMenu && (
          <Menu.Item
            onClick={() => {
              setActionsMenuOpen(false);
              setResendAccessConfirmOpen(true);
            }}
            fontSize={14}
          >
            Resend request to access results
          </Menu.Item>
        )}
        {showRequestAccessInMenu && (
          <Menu.Item
            onClick={() => {
              setActionsMenuOpen(false);
              setRequestAccessOpen(true);
            }}
            fontSize={14}
          >
            Request access
          </Menu.Item>
        )}
        {showResendVerificationInMenu && (
          <Menu.Item
            onClick={() => {
              setActionsMenuOpen(false);
              setResendVerificationConfirmOpen(true);
            }}
            fontSize={14}
          >
            Send platform invite
          </Menu.Item>
        )}
        {showRemoveFromOrgMenu && (
          <Menu.Item
            onClick={handleRemoveFromOrganisation}
            fontSize={14}
            fontFamily="gilroyMedium"
            color="error"
          >
            Remove from organisation
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );

  const profileBtn = (
    <ButtonV2
      rightIcon={<ChevronComponent />}
      href={profileUrl || undefined}
      color={granted ? "green" : "grey"}
      size={["xs", "sm", "sm"]}
      underline={false}
      key="profile"
      as={profileUrl ? ExternalTextLink : undefined}
      disabled={!granted}
      onClick={e => {
        if (!granted) {
          e.preventDefault();
        }
      }}
      sx={{
        mr: menuButton ? 0 : 60
      }}
    >
      profile
    </ButtonV2>
  );

  buttons.push(profileBtn);
  buttons.push(menuButton);

  return (
    <>
      {showRequestAccessInMenu && (
        <RequestAccessToResults
          id={id}
          patientProfile={patientProfile}
          show={requestAccessOpen}
          onClose={() => setRequestAccessOpen(false)}
        />
      )}
      {showRemoveFromOrgMenu && (
        <RemovePatientFromOrganisationModal
          show={removePatientConfirmOpen}
          onClose={() => setRemovePatientConfirmOpen(false)}
          onConfirm={confirmRemovePatientFromOrganisation}
          loading={removingPatient}
        />
      )}
      {showResendInMenu && (
        <MenuActionConfirmModal
          show={resendAccessConfirmOpen}
          onClose={() => setResendAccessConfirmOpen(false)}
          onConfirm={confirmResendAccessRequest}
          loading={resendingAccessRequest}
          title="Request access"
          description="Once confirmed we will email this patient to request access to their results on your behalf"
          confirmLabel="confirm & send"
          dataComponentName="ResendRequestToAccessResultsConfirmModal"
        />
      )}
      {showResendVerificationInMenu && (
        <MenuActionConfirmModal
          show={resendVerificationConfirmOpen}
          onClose={() => setResendVerificationConfirmOpen(false)}
          onConfirm={confirmResendVerificationEmail}
          loading={resendingVerification}
          title="Patient invite"
          description={`Once confirmed we will invite this patient to sign up and gain access to the patient platform where they will be able to see orders you place on their behalf and results or reports that you release`}
          confirmLabel="confirm & send"
          dataComponentName="ResendPlatformInviteConfirmModal"
        />
      )}
      <Group gap={10} alignItems="center" mx={[0, 0, 2]}>
        {buttons}
      </Group>
    </>
  );
}

function ConnectedUserCard({
  patientProfile,
  email,
  granted,
  id,
  status,
  created,
  relatedResultsAccessLinkId,
  hasShippingAddress,
  isAdmin,
  invited
}) {
  return (
    <Box
      pt={2}
      pb={20}
      display="flex"
      flexDirection="column"
      gap={20}
      borderColor="haze"
      borderBottomStyle="solid"
      borderWidth={1}
    >
      {renderers.personalDetails({
        id,
        email,
        patientProfile,
        granted,
        hasShippingAddress,
        isAdmin
      })}
      <Box display="flex">
        {renderers.results({
          id,
          patientProfile,
          relatedResultsAccessLinkId,
          granted,
          isAdmin,
          guardianIsActive: patientProfile?.guardian?.user?.isActive
        })}
      </Box>
      <Group gap={10} alignItems="center">
        <SpanText fontFamily="gilroyBold">Result access</SpanText>
        {renderers.resultAccess({
          granted,
          relatedResultsAccessLinkId,
          invited
        })}
      </Group>
      <Group gap={10} alignItems="center">
        <SpanText fontFamily="gilroyBold">Patient portal</SpanText>
        {renderers.patientPortal({
          isActive: patientProfile?.user?.isActive,
          guardianIsActive: patientProfile?.guardian?.user?.isActive
        })}
      </Group>
    </Box>
  );
}

const SORT_OPTIONS = [
  {
    label: "Date order (Newest - Oldest)",
    value: "-created"
  },
  {
    label: "Date order (Oldest - Newest)",
    value: "created"
  },
  {
    label: "Status (Ascending)",
    value: "status"
  },
  {
    label: "Status (Descending)",
    value: "-status"
  }
];

function ConnectedUsers() {
  const { organisation } = useOrganisationContext();

  const {
    page,
    sort,
    debouncedSearch,
    search,
    setSearch,
    setPage,
    setSort,
    sortMenuOpen,
    setSortMenuOpen
  } = useTableControls({
    sortOptions: SORT_OPTIONS
  });

  const { data, loading } = useDataLoader({
    query: CONNECTED_USERS_QUERY,
    variables: {
      page,
      orderBy: sort,
      search: debouncedSearch,
      organisationId: organisation?.id
    }
  });

  const columns = useMemo(
    () => [
      {
        id: "email",
        label: "Name / Email",
        accessor: ({ id, email, patientProfile, granted, hasShippingAddress, isAdmin }) => {
          return renderers.personalDetails({
            id,
            email,
            patientProfile,
            granted,
            hasShippingAddress,
            isAdmin
          });
        }
      },
      {
        id: "Result access",
        label: "Result access",
        accessor: ({ granted, invited, relatedResultsAccessLinkId }) =>
          renderers.resultAccess({ granted, invited, relatedResultsAccessLinkId })
      },
      {
        id: "Patient portal",
        label: "Patient portal",
        accessor: ({
          patientProfile: {
            user: { isActive },
            guardian
          }
        }) => {
          return renderers.patientPortal({ isActive, guardianIsActive: guardian?.user?.isActive });
        }
      },
      {
        id: "results",
        accessor: row => <Group justifyContent="flex-end">{renderers.results(row)}</Group>,
        label: "",
        align: "center",
        width: "25%"
      }
    ],
    []
  );

  const items = data?.connectedUsersPagination?.items;
  const pageInfo = data?.connectedUsersPagination?.pageInfo;

  const settingsUrl = getCmpUrl(CMP_BUSINESS_TAB_URL, {
    organisationId: organisation.hashId,
    tab: "settings"
  });

  return (
    <PanelBoxV2 maxWidth={1280}>
      <PanelBoxV2
        maxWidth={1020}
        outer={{
          pt: [30, 30, 60],
          pb: [50, 50, 80],
          bg: "white",
          px: 20,
          borderRadius: 5,
          "data-component-name": "ConnectedUsers"
        }}
      >
        <Stack gap={16}>
          <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]} lineHeight="130%">
            Organisation patients
          </Box>
          <Box mb={40}>
            Manage your patient connections, result access and patient platform access.
            <br />
            <br />
            New patients linked to an order will initially show a Pending result access status until
            results become available. This helps protect historical patient data unless explicit
            access has been granted by the patient via an invite and consent flow.
            <br />
            <br />
            Patient Platform Access is optional and provides a secure space to share results and
            reports with patients. Results are only visible once released by you. If platform access
            is not enabled, sharing and managing results remains the responsibility of the
            practitioner.
            <br />
            <br />
            You can manage default patient notifications and communication preferences within{" "}
            <InternalTextLink href={settingsUrl}>Notification Settings</InternalTextLink>.
          </Box>
        </Stack>
        <SimpleTable
          search={search}
          setSearch={setSearch}
          sort={sort}
          setSort={setSort}
          sortMenuOpen={sortMenuOpen}
          setSortMenuOpen={setSortMenuOpen}
          pageInfo={pageInfo}
          setPage={setPage}
          sortingOptions={SORT_OPTIONS}
          table={
            <>
              <Box display={["none", "none", "block"]}>
                <Table columns={columns} loading={loading} rows={items} />
              </Box>
              <List items={items} loading={loading} display={["block", "block", "none"]}>
                <ListContext.Consumer>
                  {items => items?.map(item => <ConnectedUserCard key={item.id} {...item} />)}
                </ListContext.Consumer>
              </List>
            </>
          }
        />
      </PanelBoxV2>
    </PanelBoxV2>
  );
}

export default ConnectedUsers;
